import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Accordion, { AccordionRow } from '../Accordion';
import SnapRow from '../SnapRow';
import ProjectVideo from '../home/ProjectVideo';
import ProductDocuments from '../ProductDocuments';
import { CheckIcon } from '../icons';
import './ProductStory.css';

// The editorial sections of a product page, below the buy box, driven by the
// product's entry in data/productContent.js. Each section is its own small
// component and renders nothing when its data is absent; ProductStory at the
// bottom composes them in page order.
//
// Every section puts each thing in the page once. Where a phone and a laptop
// need a different layout, CSS rearranges the same markup - nothing is
// rendered twice and hidden, which doubled the content crawlers read.
//
// Photography is optional. An image that fails to load marks its container
// `is-missing`, which hides the img and leaves the copy readable.

function onMissing(e) {
  const box = e.currentTarget.closest('[data-img-box]');
  if (box) box.classList.add('is-missing');
}

function ArrowLink({ to, href, children }) {
  const arrow = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14m0 0-6-6m6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
  if (href) return <a href={href} className="ps-link">{children}{arrow}</a>;
  return <Link to={to} className="ps-link">{children}{arrow}</Link>;
}

// -- Why: six benefit cards, then one line of certification -----------------------

function CertificationLine({ lead, marks = [], items = [], download, documents = [] }) {
  const doc = download ? documents.find((d) => download.match.test(d.label || '')) : null;
  return (
    <div className="ps-certline">
      <p className="ps-certline-text">
        <strong>{lead}:</strong> {marks.join(' · ')}
        {doc && <> · <a href={doc.url} target="_blank" rel="noopener noreferrer" download>{download.label}</a></>}
      </p>
      {items.length > 0 && (
        <details className="ps-certline-more">
          <summary>What each one tests</summary>
          <ul className="ps-certline-list">
            {items.map((c) => (
              <li key={c.mark}><strong>{c.mark}</strong> ({c.by}) - {c.detail}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function ProductWhy({ title, cards = [], certifications, documents }) {
  if (!cards.length) return null;
  return (
    <section className="ps-section ps-why">
      <div className="ps-inner">
        <h2 className="ps-title">{title}</h2>
        {/* Phone: swipe through the cards. Laptop: all six as a 3 x 2 grid. */}
        <SnapRow
          items={cards}
          ariaLabel={title}
          itemWidth={{ base: '80%', md: 'calc(50% - 9px)', lg: 'calc(33.333% - 12px)' }}
          gap={18}
          wrap="lg"
          bleed="var(--ps-pad)"
          arrows={false}
          renderItem={(c) => (
            <article className="ps-card">
              <div className="ps-card-img" data-img-box>
                <img src={c.image} alt="" loading="lazy" onError={onMissing} />
              </div>
              <h3>{c.heading}</h3>
              <p>{c.text}</p>
              {c.link && <ArrowLink to={c.link.to}>{c.link.label}</ArrowLink>}
            </article>
          )}
        />
        {certifications && <CertificationLine {...certifications} documents={documents} />}
      </div>
    </section>
  );
}

// -- Comparison: one table; on a phone each row stacks into a card --------------

export function ProductComparison({ title, subtitle, columns = [], rows = [] }) {
  if (!rows.length) return null;
  const [factorCol, usCol, themCol] = columns;
  return (
    <section className="ps-section ps-diff">
      <div className="ps-inner">
        <h2 className="ps-title">{title}</h2>
        {subtitle && <p className="ps-subtitle">{subtitle}</p>}
        <table className="ps-diff-table">
          <thead>
            <tr>
              <th scope="col">{factorCol}</th>
              <th scope="col" className="ps-diff-us">{usCol}</th>
              <th scope="col">{themCol}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([factor, us, them]) => (
              <tr key={factor}>
                <th scope="row">{factor}</th>
                {/* data-label carries the column name into the stacked phone
                    layout, where the header row is not shown. */}
                <td className="ps-diff-us" data-label={usCol}>
                  <span className="ps-diff-us-in"><CheckIcon /><span>{us}</span></span>
                </td>
                <td className="ps-diff-them" data-label={themCol}>{them}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// -- Video: the demonstration clip and a caption ----------------------------------

export function ProductVideoSection({ title, text, mp4, webm, poster }) {
  if (!mp4 && !webm) return null;
  return (
    <section className="ps-section ps-video">
      <div className="ps-inner">
        <div className="ps-video-grid">
          <div className="ps-video-copy">
            <h2 className="ps-title">{title}</h2>
            {text && <p className="ps-subtitle">{text}</p>}
          </div>
          <div className="ps-video-media">
            <ProjectVideo video={{ mp4, webm, poster }} title={title} />
          </div>
        </div>
      </div>
    </section>
  );
}

// -- Installation: four lines, the guide links, the install video ----------------

export function ProductInstallation({ title, lines = [], links = [], video, videoLinkLabel }) {
  if (!lines.length) return null;
  const videoId = 'install-video';
  return (
    <section className="ps-section ps-install">
      <div className="ps-inner">
        <div className={`ps-install-grid${video ? ' ps-install-grid--video' : ''}`}>
          <div className="ps-install-copy">
            <h2 className="ps-title">{title}</h2>
            <ul className="ps-install-lines">
              {lines.map((l) => <li key={l}><CheckIcon />{l}</li>)}
            </ul>
            <div className="ps-links">
              {links.map((l) => <ArrowLink key={l.to} to={l.to}>{l.label}</ArrowLink>)}
              {video && videoLinkLabel && <ArrowLink href={`#${videoId}`}>{videoLinkLabel}</ArrowLink>}
            </div>
          </div>
          {video && (
            <figure className="ps-install-video" id={videoId}>
              <ProjectVideo video={video} title={video.title || title} mode="guide" />
            </figure>
          )}
        </div>
      </div>
    </section>
  );
}

// -- Specifications: one grouped table, the downloads under it --------------------
//
// Always in the page, never behind a toggle: a spec table that only appears
// on click is an empty section to a crawler.

export function ProductSpecs({ groups = [], documents, id = 'specifications' }) {
  if (!groups.length) return null;
  return (
    <section className="ps-section ps-specs" id={id}>
      <div className="ps-inner ps-inner--narrow">
        <h2 className="ps-title">Specifications</h2>
        <table className="ps-spec-table">
          {groups.map((g) => (
            <tbody key={g.group}>
              <tr className="ps-spec-group"><th colSpan={2} scope="colgroup">{g.group}</th></tr>
              {g.rows.map(([k, v]) => (
                <tr key={k}><th scope="row">{k}</th><td>{v}</td></tr>
              ))}
            </tbody>
          ))}
        </table>
        <ProductDocuments
          documents={documents}
          title="Downloads"
          subtitle="The wind report, installation guide and datasheet, free to download."
        />
      </div>
    </section>
  );
}

// -- Reviews: real ones, or real installs until there are some --------------------
//
// Never placeholder reviews. With none to show, the block is photos of the
// product on customers' homes, captioned with the suburb.

function Stars({ rating }) {
  const full = Math.round(rating);
  return <span className="ps-stars" aria-label={`${rating} out of 5`}>{'★★★★★'.slice(0, full)}<span className="ps-stars-off">{'★★★★★'.slice(full)}</span></span>;
}

export function ProductReviews({ title, rating, count, url, items = [], installs = [] }) {
  const hasReviews = items.length > 0;
  if (!hasReviews && !installs.length) return null;
  return (
    <section className="ps-section ps-reviews">
      <div className="ps-inner">
        <h2 className="ps-title">{hasReviews ? title : 'On customers\' homes'}</h2>
        {hasReviews && rating != null && (
          <p className="ps-subtitle">
            <Stars rating={rating} /> {rating.toFixed(1)} from {count} reviews
            {url && <> · <a href={url} target="_blank" rel="noopener noreferrer">Read them on Google</a></>}
          </p>
        )}
        {hasReviews ? (
          <ul className="ps-review-grid">
            {items.map((r) => (
              <li key={`${r.name}-${r.text.slice(0, 20)}`} className="ps-review">
                {r.image && <img src={r.image} alt={`AW01 at ${r.name}'s home in ${r.suburb}`} loading="lazy" />}
                {r.rating != null && <Stars rating={r.rating} />}
                <p className="ps-review-text">{r.text}</p>
                <p className="ps-review-by">{r.name}, {r.suburb}</p>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="ps-install-photos">
            {installs.map((it) => (
              <li key={it.image}>
                <figure data-img-box>
                  <img src={it.image} alt={`AW01 installed in ${it.suburb}`} loading="lazy" onError={onMissing} />
                  <figcaption>{it.suburb}</figcaption>
                </figure>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// -- FAQ: grouped questions under tabs -----------------------------------------------
//
// Every group is in the page; the tabs only choose which one is shown. The
// answers were previously rendered only for the open tab and the open
// question, so the page's FAQ markup listed questions the HTML never answered.
// `children` renders under the questions - the quick-question form.

export function ProductFaq({ groups = [], children }) {
  const [activeGroup, setActiveGroup] = useState(0);
  if (!groups.length) return null;
  return (
    <section className="ps-section ps-faq">
      <div className="ps-inner ps-inner--narrow">
        <h2 className="ps-title">Questions, answered</h2>
        <div className="ps-faq-tabs" role="tablist" aria-label="Question topics">
          {groups.map((grp, i) => (
            <button
              key={grp.group}
              type="button"
              role="tab"
              id={`faq-tab-${i}`}
              aria-controls={`faq-panel-${i}`}
              aria-selected={i === activeGroup}
              className={`ps-faq-tab${i === activeGroup ? ' ps-faq-tab--active' : ''}`}
              onClick={() => setActiveGroup(i)}
            >
              {grp.group}
            </button>
          ))}
        </div>
        {groups.map((grp, i) => (
          <div key={grp.group} id={`faq-panel-${i}`} role="tabpanel" aria-labelledby={`faq-tab-${i}`} hidden={i !== activeGroup}>
            <Accordion className="ps-faq-list">
              {grp.items.map((it) => (
                <AccordionRow key={it.q} title={it.q} headingLevel={3}>
                  <p>{it.a}</p>
                </AccordionRow>
              ))}
            </Accordion>
          </div>
        ))}
        {children && <div className="ps-faq-footer">{children}</div>}
      </div>
    </section>
  );
}

// -- Composite -------------------------------------------------------------------------

export default function ProductStory({ content, documents, faqFooter }) {
  if (!content) return null;
  return (
    <>
      {content.why && <ProductWhy {...content.why} documents={documents} />}
      {content.comparison && <ProductComparison {...content.comparison} />}
      {content.video && <ProductVideoSection {...content.video} />}
      {content.installation && <ProductInstallation {...content.installation} />}
      {content.specs && <ProductSpecs groups={content.specs} documents={documents} />}
      {content.reviews && <ProductReviews {...content.reviews} />}
      {content.faq && <ProductFaq groups={content.faq}>{faqFooter}</ProductFaq>}
    </>
  );
}
